import React, { useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  Button,
  IconButton,
  Divider,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  School as SchoolIcon,
  Verified as VerifiedIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { caregiverProfileService } from '../../services/caregiverProfileService';

interface Certification {
  id: string;
  name: string;
  issuer: string;
  issueDate: string | null;
  expiryDate: string | null;
  credentialId: string | null;
  verified: boolean;
  documentUrl: string | null;
  documentName: string | null;
  createdAt: string;
  updatedAt: string;
}

interface CertificationsManagementSidebarProps {
  open: boolean;
  onClose: () => void;
  certifications: Certification[];
}

interface CertificationFormData {
  name: string;
  issuer: string;
  issueDate: string;
  expiryDate: string;
  credentialId: string;
  documentUrl: string;
  documentName: string;
}

const CertificationsManagementSidebar: React.FC<CertificationsManagementSidebarProps> = ({
  open,
  onClose,
  certifications,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showCertificationDialog, setShowCertificationDialog] = useState(false);
  const [editingCertification, setEditingCertification] = useState<Certification | null>(null);
  const [certificationForm, setCertificationForm] = useState<CertificationFormData>({
    name: '',
    issuer: '',
    issueDate: '',
    expiryDate: '',
    credentialId: '',
    documentUrl: '',
    documentName: '',
  });

  const handleAddCertification = () => {
    setEditingCertification(null);
    setCertificationForm({
      name: '',
      issuer: '',
      issueDate: '',
      expiryDate: '',
      credentialId: '',
      documentUrl: '',
      documentName: '',
    });
    setShowCertificationDialog(true);
  };

  const handleEditCertification = (certification: Certification) => {
    setEditingCertification(certification);
    setCertificationForm({
      name: certification.name,
      issuer: certification.issuer,
      issueDate: certification.issueDate ? certification.issueDate.split('T')[0] : '',
      expiryDate: certification.expiryDate ? certification.expiryDate.split('T')[0] : '',
      credentialId: certification.credentialId || '',
      documentUrl: certification.documentUrl || '',
      documentName: certification.documentName || '',
    });
    setShowCertificationDialog(true);
  };

  const handleDeleteCertification = async (certificationId: string) => {
    if (!window.confirm('Are you sure you want to delete this certification?')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await caregiverProfileService.deleteCertification(certificationId);
      setSuccess('Certification deleted successfully');
      onClose(); // Refresh the parent component
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete certification');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCertification = async () => {
    try {
      setLoading(true);
      setError(null);

      const formData = {
        ...certificationForm,
        issueDate: certificationForm.issueDate?.trim() || undefined,
        expiryDate: certificationForm.expiryDate?.trim() || undefined,
        credentialId: certificationForm.credentialId?.trim() || undefined,
        documentUrl: certificationForm.documentUrl?.trim() || undefined,
        documentName: certificationForm.documentName?.trim() || undefined,
      };

      if (editingCertification) {
        await caregiverProfileService.updateCertification(editingCertification.id, formData);
        setSuccess('Certification updated successfully');
      } else {
        await caregiverProfileService.createCertification(formData);
        setSuccess('Certification created successfully');
      }

      setShowCertificationDialog(false);
      onClose(); // Refresh the parent component
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save certification');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError(null);
    setSuccess(null);
    onClose();
  };

  const handleCertificationFormChange = (field: keyof CertificationFormData, value: string) => {
    setCertificationForm(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString();
  };

  const isExpired = (expiryDate: string | null) => {
    if (!expiryDate) return false;
    return new Date(expiryDate) < new Date();
  };

  const isValid = certificationForm.name.trim() && certificationForm.issuer.trim();

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        sx={{
          '& .MuiDrawer-paper': {
            width: isMobile ? '100%' : '40%',
            maxWidth: 600,
          },
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">Manage Certifications</Typography>
              <IconButton onClick={handleClose}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Add and manage your professional certifications and licenses.
            </Typography>
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, p: 3, overflow: 'auto' }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddCertification}
              fullWidth
              sx={{ mb: 3 }}
            >
              Add New Certification
            </Button>

            {certifications.length > 0 ? (
              <List>
                {certifications.map((cert) => (
                  <ListItem key={cert.id} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, mb: 1 }}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="subtitle1">{cert.name}</Typography>
                          {cert.verified && (
                            <Chip
                              icon={<VerifiedIcon />}
                              label="Verified"
                              size="small"
                              color="success"
                            />
                          )}
                          {isExpired(cert.expiryDate) && (
                            <Chip
                              label="Expired"
                              size="small"
                              color="error"
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Issued by: {cert.issuer}
                          </Typography>
                          {cert.credentialId && (
                            <Typography variant="body2" color="text.secondary">
                              ID: {cert.credentialId}
                            </Typography>
                          )}
                          <Typography variant="body2" color="text.secondary">
                            Issued: {formatDate(cert.issueDate)}
                            {cert.expiryDate && ` • Expires: ${formatDate(cert.expiryDate)}`}
                          </Typography>
                          {cert.documentUrl && (
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                              <LinkIcon sx={{ fontSize: 16, mr: 0.5 }} />
                              <Typography variant="body2" color="primary">
                                {cert.documentName || 'View Document'}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleEditCertification(cert)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteCertification(cert.id)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <SchoolIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="body1" color="text.secondary">
                  No certifications added yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Click "Add New Certification" to get started
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Drawer>

      {/* Certification Form Dialog */}
      <Dialog
        open={showCertificationDialog}
        onClose={() => setShowCertificationDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingCertification ? 'Edit Certification' : 'Add New Certification'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              fullWidth
              label="Certification Name"
              value={certificationForm.name}
              onChange={(e) => handleCertificationFormChange('name', e.target.value)}
              placeholder="e.g., Licensed Physical Therapist"
              required
            />

            <TextField
              fullWidth
              label="Issuing Organization"
              value={certificationForm.issuer}
              onChange={(e) => handleCertificationFormChange('issuer', e.target.value)}
              placeholder="e.g., American Physical Therapy Association"
              required
            />

            <TextField
              fullWidth
              label="Credential ID / License Number"
              value={certificationForm.credentialId}
              onChange={(e) => handleCertificationFormChange('credentialId', e.target.value)}
              placeholder="e.g., LPT-12345"
            />

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Issue Date"
                  value={certificationForm.issueDate}
                  onChange={(e) => handleCertificationFormChange('issueDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Expiry Date"
                  value={certificationForm.expiryDate}
                  onChange={(e) => handleCertificationFormChange('expiryDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>

            <TextField
              fullWidth
              label="Document URL"
              value={certificationForm.documentUrl}
              onChange={(e) => handleCertificationFormChange('documentUrl', e.target.value)}
              placeholder="https://example.com/certificate.pdf"
              helperText="Optional: Link to verification document or certificate"
            />

            <TextField
              fullWidth
              label="Document Name"
              value={certificationForm.documentName}
              onChange={(e) => handleCertificationFormChange('documentName', e.target.value)}
              placeholder="e.g., Physical Therapy License Certificate"
              helperText="Optional: Name of the uploaded document"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCertificationDialog(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
            onClick={handleSaveCertification}
            disabled={loading || !isValid}
          >
            {loading ? 'Saving...' : editingCertification ? 'Update Certification' : 'Add Certification'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CertificationsManagementSidebar;
